<template>
    <v-dialog v-model="visible" fullscreen scrollable transition="dialog-bottom-transition">
        <v-card class="hardware-card">
            <!-- Header -->
            <div class="card-header">
                <div class="left-section">
                    <div class="header-badge">{{ $t('hardwareControl.header.badge') }}</div>
                    <h2>{{ $t('hardwareControl.header.title') }}</h2>
                    <p class="header-description">
                        {{ $t('hardwareControl.header.description') }}
                    </p>
                </div>
                <div class="right-section">
                    <button @click="closeDialog" class="dialog-close-btn">
                        <v-icon>mdi-close</v-icon>
                    </button>
                </div>
            </div>

            <!-- Main Content -->
            <div class="hardware-container">
                <!-- Control Flow Diagram -->
                <div class="flow-section">
                    <div class="section-header">
                        <h3>{{ $t('hardwareControl.controlArchitecture.title') }}</h3>
                        <p>{{ $t('hardwareControl.controlArchitecture.subtitle') }}</p>
                    </div>

                    <div class="flow-diagram">
                        <!-- User Layer -->
                        <div class="flow-node user-node">
                            <div class="node-icon">
                                <v-icon size="40" color="white">mdi-account-circle</v-icon>
                            </div>
                            <div class="node-content">
                                <h4>{{ $t('hardwareControl.controlArchitecture.endUser.title') }}</h4>
                                <p>{{ $t('hardwareControl.controlArchitecture.endUser.description') }}</p>
                            </div>
                        </div>

                        <!-- Arrow -->
                        <div class="flow-connector">
                            <div class="connector-line"></div>
                            <div class="connector-label">
                                <v-icon size="16">mdi-security</v-icon>
                                <span>{{ $t('hardwareControl.controlArchitecture.connectors.authenticated') }}</span>
                            </div>
                            <v-icon class="connector-arrow" size="24">mdi-arrow-down-circle</v-icon>
                        </div>

                        <!-- Mobile App Layer -->
                        <div class="flow-node app-node">
                            <div class="node-icon">
                                <v-icon size="40" color="white">mdi-cellphone</v-icon>
                            </div>
                            <div class="node-content">
                                <h4>{{ $t('hardwareControl.controlArchitecture.mobileApp.title') }}</h4>
                                <p>{{ $t('hardwareControl.controlArchitecture.mobileApp.description') }}</p>
                            </div>
                            <div class="node-features">
                                <div class="feature-chip">{{ $t('hardwareControl.controlArchitecture.mobileApp.feature1') }}</div>
                                <div class="feature-chip">{{ $t('hardwareControl.controlArchitecture.mobileApp.feature2') }}</div>
                                <div class="feature-chip">{{ $t('hardwareControl.controlArchitecture.mobileApp.feature3') }}</div>
                            </div>
                        </div>

                        <!-- Arrow -->
                        <div class="flow-connector">
                            <div class="connector-line"></div>
                            <div class="connector-label">
                                <v-icon size="16">mdi-wifi</v-icon>
                                <span>{{ $t('hardwareControl.controlArchitecture.connectors.encrypted') }}</span>
                            </div>
                            <v-icon class="connector-arrow" size="24">mdi-arrow-down-circle</v-icon>
                        </div>

                        <!-- Hub Layer -->
                        <div class="flow-node hub-node">
                            <div class="node-icon">
                                <v-icon size="40" color="white">mdi-server-network</v-icon>
                            </div>
                            <div class="node-content">
                                <h4>{{ $t('hardwareControl.controlArchitecture.smartHub.title') }}</h4>
                                <p>{{ $t('hardwareControl.controlArchitecture.smartHub.description') }}</p>
                            </div>
                            <div class="node-specs">
                                <div class="spec-item">
                                    <v-icon size="16">mdi-check</v-icon>
                                    <span>{{ $t('hardwareControl.controlArchitecture.smartHub.integration1') }}</span>
                                </div>
                                <div class="spec-item">
                                    <v-icon size="16">mdi-check</v-icon>
                                    <span>{{ $t('hardwareControl.controlArchitecture.smartHub.integration2') }}</span>
                                </div>
                                <div class="spec-item">
                                    <v-icon size="16">mdi-check</v-icon>
                                    <span>{{ $t('hardwareControl.controlArchitecture.smartHub.integration3') }}</span>
                                </div>
                                <div class="spec-item">
                                    <v-icon size="16">mdi-check</v-icon>
                                    <span>{{ $t('hardwareControl.controlArchitecture.smartHub.integration4') }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Arrow -->
                        <div class="flow-connector">
                            <div class="connector-line multi-line"></div>
                            <div class="connector-label">
                                <v-icon size="16">mdi-network</v-icon>
                                <span>{{ $t('hardwareControl.controlArchitecture.connectors.network') }}</span>
                            </div>
                            <v-icon class="connector-arrow" size="24">mdi-arrow-down-circle</v-icon>
                        </div>

                        <!-- Devices Layer -->
                        <div class="devices-layer">
                            <div class="device-mini">
                                <v-icon size="20" color="white">mdi-lightbulb</v-icon>
                            </div>
                            <div class="device-mini">
                                <v-icon size="20" color="white">mdi-thermometer</v-icon>
                            </div>
                            <div class="device-mini">
                                <v-icon size="20" color="white">mdi-air-filter</v-icon>
                            </div>
                            <div class="device-mini">
                                <v-icon size="20" color="white">mdi-blinds</v-icon>
                            </div>
                            <div class="device-mini">
                                <v-icon size="20" color="white">mdi-door</v-icon>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Device Categories -->
                <div class="devices-section">
                    <div class="section-header">
                        <h3>{{ $t('hardwareControl.controllableSystems.title') }}</h3>
                        <p>{{ $t('hardwareControl.controllableSystems.subtitle') }}</p>
                    </div>

                    <v-row class="devices-grid">
                        <!-- Lighting -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="device-card lighting">
                                <div class="device-header">
                                    <div class="device-icon">
                                        <v-icon size="36" color="white">mdi-lightbulb-on</v-icon>
                                    </div>
                                    <div class="device-status online">
                                        <span class="status-dot"></span>
                                        <span>{{ $t('hardwareControl.controllableSystems.lighting.deviceCount') }}</span>
                                    </div>
                                </div>
                                <h4>{{ $t('hardwareControl.controllableSystems.lighting.title') }}</h4>
                                <p>{{ $t('hardwareControl.controllableSystems.lighting.description') }}</p>
                                <div class="device-features">
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.lighting.feature1') }}</span>
                                    </div>
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.lighting.feature2') }}</span>
                                    </div>
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.lighting.feature3') }}</span>
                                    </div>
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.lighting.feature4') }}</span>
                                    </div>
                                </div>
                                <div class="device-stats">
                                    <div class="stat">
                                        <span class="stat-label">{{ $t('hardwareControl.controllableSystems.lighting.stats.zones') }}</span>
                                        <span class="stat-value">32</span>
                                    </div>
                                    <div class="stat">
                                        <span class="stat-label">{{ $t('hardwareControl.controllableSystems.lighting.stats.efficiency') }}</span>
                                        <span class="stat-value">89%</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- HVAC -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="device-card hvac">
                                <div class="device-header">
                                    <div class="device-icon">
                                        <v-icon size="36" color="white">mdi-thermostat</v-icon>
                                    </div>
                                    <div class="device-status online">
                                        <span class="status-dot"></span>
                                        <span>{{ $t('hardwareControl.controllableSystems.hvac.deviceCount') }}</span>
                                    </div>
                                </div>
                                <h4>{{ $t('hardwareControl.controllableSystems.hvac.title') }}</h4>
                                <p>{{ $t('hardwareControl.controllableSystems.hvac.description') }}</p>
                                <div class="device-features">
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.hvac.feature1') }}</span>
                                    </div>
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.hvac.feature2') }}</span>
                                    </div>
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.hvac.feature3') }}</span>
                                    </div>
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.hvac.feature4') }}</span>
                                    </div>
                                </div>
                                <div class="device-stats">
                                    <div class="stat">
                                        <span class="stat-label">{{ $t('hardwareControl.controllableSystems.hvac.stats.temperature') }}</span>
                                        <span class="stat-value">22.5°C</span>
                                    </div>
                                    <div class="stat">
                                        <span class="stat-label">{{ $t('hardwareControl.controllableSystems.hvac.stats.humidity') }}</span>
                                        <span class="stat-value">45%</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Ventilation -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="device-card ventilation">
                                <div class="device-header">
                                    <div class="device-icon">
                                        <v-icon size="36" color="white">mdi-air-filter</v-icon>
                                    </div>
                                    <div class="device-status online">
                                        <span class="status-dot"></span>
                                        <span>{{ $t('hardwareControl.controllableSystems.ventilation.deviceCount') }}</span>
                                    </div>
                                </div>
                                <h4>{{ $t('hardwareControl.controllableSystems.ventilation.title') }}</h4>
                                <p>{{ $t('hardwareControl.controllableSystems.ventilation.description') }}</p>
                                <div class="device-features">
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.ventilation.feature1') }}</span>
                                    </div>
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.ventilation.feature2') }}</span>
                                    </div>
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.ventilation.feature3') }}</span>
                                    </div>
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.ventilation.feature4') }}</span>
                                    </div>
                                </div>
                                <div class="device-stats">
                                    <div class="stat">
                                        <span class="stat-label">{{ $t('hardwareControl.controllableSystems.ventilation.stats.airQuality') }}</span>
                                        <span class="stat-value">{{ $t('hardwareControl.controllableSystems.ventilation.stats.airQuality') }}</span>
                                    </div>
                                    <div class="stat">
                                        <span class="stat-label">{{ $t('hardwareControl.controllableSystems.ventilation.stats.co2Level') }}</span>
                                        <span class="stat-value">420 ppm</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Blinds -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="device-card blinds">
                                <div class="device-header">
                                    <div class="device-icon">
                                        <v-icon size="36" color="white">mdi-blinds</v-icon>
                                    </div>
                                    <div class="device-status online">
                                        <span class="status-dot"></span>
                                        <span>{{ $t('hardwareControl.controllableSystems.blinds.deviceCount') }}</span>
                                    </div>
                                </div>
                                <h4>{{ $t('hardwareControl.controllableSystems.blinds.title') }}</h4>
                                <p>{{ $t('hardwareControl.controllableSystems.blinds.description') }}</p>
                                <div class="device-features">
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.blinds.feature1') }}</span>
                                    </div>
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.blinds.feature2') }}</span>
                                    </div>
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.blinds.feature3') }}</span>
                                    </div>
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.blinds.feature4') }}</span>
                                    </div>
                                </div>
                                <div class="device-stats">
                                    <div class="stat">
                                        <span class="stat-label">{{ $t('hardwareControl.controllableSystems.blinds.stats.position') }}</span>
                                        <span class="stat-value">65%</span>
                                    </div>
                                    <div class="stat">
                                        <span class="stat-label">Solar Gain</span>
                                        <span class="stat-value">{{ $t('hardwareControl.controllableSystems.blinds.stats.solarGain') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Access Control -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="device-card access">
                                <div class="device-header">
                                    <div class="device-icon">
                                        <v-icon size="36" color="white">mdi-lock-smart</v-icon>
                                    </div>
                                    <div class="device-status online">
                                        <span class="status-dot"></span>
                                        <span>{{ $t('hardwareControl.controllableSystems.access.deviceCount') }}</span>
                                    </div>
                                </div>
                                <h4>{{ $t('hardwareControl.controllableSystems.access.title') }}</h4>
                                <p>{{ $t('hardwareControl.controllableSystems.access.description') }}</p>
                                <div class="device-features">
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.access.feature1') }}</span>
                                    </div>
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.access.feature2') }}</span>
                                    </div>
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.access.feature3') }}</span>
                                    </div>
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.access.feature4') }}</span>
                                    </div>
                                </div>
                                <div class="device-stats">
                                    <div class="stat">
                                        <span class="stat-label">{{ $t('hardwareControl.controllableSystems.access.stats.activeUsers') }}</span>
                                        <span class="stat-value">1,247</span>
                                    </div>
                                    <div class="stat">
                                        <span class="stat-label">{{ $t('hardwareControl.controllableSystems.access.stats.accessEvents') }}</span>
                                        <span class="stat-value">892</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Energy Monitoring -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="device-card energy">
                                <div class="device-header">
                                    <div class="device-icon">
                                        <v-icon size="36" color="white">mdi-flash</v-icon>
                                    </div>
                                    <div class="device-status online">
                                        <span class="status-dot"></span>
                                        <span>{{ $t('hardwareControl.controllableSystems.energy.deviceCount') }}</span>
                                    </div>
                                </div>
                                <h4>{{ $t('hardwareControl.controllableSystems.energy.title') }}</h4>
                                <p>{{ $t('hardwareControl.controllableSystems.energy.description') }}</p>
                                <div class="device-features">
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.energy.feature1') }}</span>
                                    </div>
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.energy.feature2') }}</span>
                                    </div>
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.energy.feature3') }}</span>
                                    </div>
                                    <div class="feature-row">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('hardwareControl.controllableSystems.energy.feature4') }}</span>
                                    </div>
                                </div>
                                <div class="device-stats">
                                    <div class="stat">
                                        <span class="stat-label">{{ $t('hardwareControl.controllableSystems.energy.stats.currentLoad') }}</span>
                                        <span class="stat-value">847 kW</span>
                                    </div>
                                    <div class="stat">
                                        <span class="stat-label">{{ $t('hardwareControl.controllableSystems.energy.stats.efficiency') }}</span>
                                        <span class="stat-value">94%</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </div>

                <!-- Control Features -->
                <div class="features-section">
                    <div class="section-header">
                        <h3>{{ $t('hardwareControl.advancedFeatures.title') }}</h3>
                        <p>{{ $t('hardwareControl.advancedFeatures.subtitle') }}</p>
                    </div>

                    <div class="features-grid">
                        <div class="control-feature">
                            <div class="feature-icon-wrapper">
                                <v-icon size="32" color="white">mdi-robot</v-icon>
                            </div>
                            <h4>{{ $t('hardwareControl.advancedFeatures.automatedScenarios.title') }}</h4>
                            <p>{{ $t('hardwareControl.advancedFeatures.automatedScenarios.description') }}</p>
                        </div>

                        <div class="control-feature">
                            <div class="feature-icon-wrapper">
                                <v-icon size="32" color="white">mdi-calendar-clock</v-icon>
                            </div>
                            <h4>{{ $t('hardwareControl.advancedFeatures.smartScheduling.title') }}</h4>
                            <p>{{ $t('hardwareControl.advancedFeatures.smartScheduling.description') }}</p>
                        </div>

                        <div class="control-feature">
                            <div class="feature-icon-wrapper">
                                <v-icon size="32" color="white">mdi-account-multiple</v-icon>
                            </div>
                            <h4>{{ $t('hardwareControl.advancedFeatures.userPreferences.title') }}</h4>
                            <p>{{ $t('hardwareControl.advancedFeatures.userPreferences.description') }}</p>
                        </div>

                        <div class="control-feature">
                            <div class="feature-icon-wrapper">
                                <v-icon size="32" color="white">mdi-bell-alert</v-icon>
                            </div>
                            <h4>{{ $t('hardwareControl.advancedFeatures.alertManagement.title') }}</h4>
                            <p>{{ $t('hardwareControl.advancedFeatures.alertManagement.description') }}</p>
                        </div>

                        <div class="control-feature">
                            <div class="feature-icon-wrapper">
                                <v-icon size="32" color="white">mdi-chart-line</v-icon>
                            </div>
                            <h4>{{ $t('hardwareControl.advancedFeatures.performanceAnalytics.title') }}</h4>
                            <p>{{ $t('hardwareControl.advancedFeatures.performanceAnalytics.description') }}</p>
                        </div>

                        <div class="control-feature">
                            <div class="feature-icon-wrapper">
                                <v-icon size="32" color="white">mdi-cloud-sync</v-icon>
                            </div>
                            <h4>{{ $t('hardwareControl.advancedFeatures.cloudIntegration.title') }}</h4>
                            <p>{{ $t('hardwareControl.advancedFeatures.cloudIntegration.description') }}</p>
                        </div>
                    </div>
                </div>

                <!-- Protocols & Standards -->
                <div class="protocols-section">
                    <div class="section-header">
                        <h3>{{ $t('hardwareControl.integrationPartners.title') }}</h3>
                        <p>{{ $t('hardwareControl.integrationPartners.subtitle') }}</p>
                    </div>

                    <div class="protocols-grid">
                        <div class="protocol-card featured">
                            <div class="protocol-logo">aedifion</div>
                            <p>{{ $t('hardwareControl.integrationPartners.partners.aedifion') }}</p>
                        </div>
                        <div class="protocol-card featured">
                            <div class="protocol-logo">Schindler</div>
                            <p>{{ $t('hardwareControl.integrationPartners.partners.schindler') }}</p>
                        </div>
                        <div class="protocol-card featured">
                            <div class="protocol-logo">BlueID</div>
                            <p>{{ $t('hardwareControl.integrationPartners.partners.blueid') }}</p>
                        </div>
                        <div class="protocol-card featured">
                            <div class="protocol-logo">KNX</div>
                            <p>{{ $t('hardwareControl.integrationPartners.partners.knx') }}</p>
                        </div>
                        <div class="protocol-card">
                            <div class="protocol-logo">MQTT</div>
                            <p>{{ $t('hardwareControl.integrationPartners.partners.mqtt') }}</p>
                        </div>
                        <div class="protocol-card">
                            <div class="protocol-logo">BACnet</div>
                            <p>{{ $t('hardwareControl.integrationPartners.partners.bacnet') }}</p>
                        </div>
                        <div class="protocol-card">
                            <div class="protocol-logo">Modbus</div>
                            <p>{{ $t('hardwareControl.integrationPartners.partners.modbus') }}</p>
                        </div>
                        <div class="protocol-card">
                            <div class="protocol-logo">REST API</div>
                            <p>{{ $t('hardwareControl.integrationPartners.partners.restapi') }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const visible = ref(false);

function open() { 
    visible.value = true; 
}

function closeDialog() { 
    visible.value = false; 
}

defineExpose({ open, close: closeDialog });
</script>
<style scoped>
/* Card Base */
.hardware-card {
    background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);
    min-height: 100vh;
    min-height: 100dvh;
    position: relative;
}

/* Header */
.card-header {
    background: linear-gradient(135deg, #0A1929 0%, #1A2F42 100%);
    padding: 32px 40px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.left-section {
    flex: 1;
}

.header-badge {
    display: inline-block;
    padding: 6px 16px;
    background: rgba(30, 90, 142, 0.2);
    border: 1px solid rgba(30, 90, 142, 0.3);
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    color: #60a5fa;
    margin-bottom: 16px;
}

.card-header h2 {
    font-size: 32px;
    font-weight: 800;
    color: #ffffff;
    margin: 0 0 8px 0;
    letter-spacing: -0.5px;
}

.header-description {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.5;
    margin: 0;
}

.right-section {
    margin-left: 24px;
}

.dialog-close-btn {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
}

.dialog-close-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
}

.dialog-close-btn .v-icon {
    color: white;
}

/* Main Container */
.hardware-container {
    padding: 40px;
    max-width: 1600px;
    margin: 0 auto;
}

/* Section Styling */
.flow-section,
.devices-section,
.features-section,
.protocols-section {
    margin-bottom: 56px;
}

.section-header {
    text-align: center;
    margin-bottom: 40px;
}

.section-header h3 {
    font-size: 32px;
    font-weight: 800;
    color: #1f2937;
    margin: 0 0 12px 0;
    letter-spacing: -0.5px;
}

.section-header p {
    font-size: 16px;
    color: #6b7280;
    margin: 0;
}

/* Flow Diagram */
.flow-diagram {
    background: white;
    border-radius: 24px;
    padding: 48px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
}

.flow-node {
    background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
    border-radius: 20px;
    padding: 32px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    border: 2px solid transparent;
    width: 100%;
    max-width: 600px;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.flow-node:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.flow-node.user-node {
    border-color: rgba(139, 92, 246, 0.2);
}

.flow-node.user-node:hover {
    border-color: #8b5cf6;
}

.flow-node.app-node {
    border-color: rgba(30, 90, 142, 0.2);
}

.flow-node.app-node:hover {
    border-color: #1E5A8E;
}

.flow-node.hub-node {
    border-color: rgba(16, 185, 129, 0.2);
}

.flow-node.hub-node:hover {
    border-color: #10b981;
}

.node-icon {
    width: 80px;
    height: 80px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    transition: transform 0.4s ease;
}

.flow-node:hover .node-icon {
    transform: scale(1.1) rotate(-5deg);
}

.user-node .node-icon {
    background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
}

.app-node .node-icon {
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
}

.hub-node .node-icon {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.node-content {
    text-align: center;
    margin-bottom: 16px;
}

.node-content h4 {
    font-size: 22px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 8px 0;
}

.node-content p {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
}

.node-features {
    display: flex;
    justify-content: center;
    gap: 8px;
    flex-wrap: wrap;
}

.feature-chip {
    padding: 6px 14px;
    background: #f3f4f6;
    color: #4b5563;
    font-size: 12px;
    font-weight: 600;
    border-radius: 20px;
}

.node-specs {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 16px;
}

.spec-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #4b5563;
    font-weight: 500;
}

.spec-item .v-icon {
    color: #10b981;
}

.flow-connector {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 24px 0;
}

.connector-line {
    width: 2px;
    height: 60px;
    background: linear-gradient(180deg, #1E5A8E 0%, rgba(30, 90, 142, 0.3) 100%);
}

.connector-line.multi-line {
    background: linear-gradient(180deg, #10b981 0%, rgba(16, 185, 129, 0.3) 100%);
}

.connector-label {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: white;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    color: #1E5A8E;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    white-space: nowrap;
}

.connector-arrow {
    color: #1E5A8E;
    margin-top: -8px;
    animation: bounce 2s ease-in-out infinite;
}

@keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(8px); }
}

.devices-layer {
    display: flex;
    gap: 16px;
    margin-top: 24px;
}

.device-mini {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: linear-gradient(135deg, #FF6B35 0%, #FFA07A 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.3s ease;
}

.device-mini:hover {
    transform: scale(1.15) rotate(5deg);
}

/* Device Cards */
.device-card {
    background: white;
    border-radius: 20px;
    padding: 28px;
    height: 100%;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    border: 2px solid transparent;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.device-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.device-card.lighting:hover { border-color: #fbbf24; }
.device-card.hvac:hover { border-color: #FF6B35; }
.device-card.ventilation:hover { border-color: #1E5A8E; }
.device-card.blinds:hover { border-color: #8b5cf6; }
.device-card.access:hover { border-color: #10b981; }
.device-card.energy:hover { border-color: #f59e0b; }

.device-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.device-icon {
    width: 72px;
    height: 72px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.4s ease;
}

.device-card:hover .device-icon {
    transform: scale(1.1) rotate(-5deg);
}

.device-card.lighting .device-icon {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
}

.device-card.hvac .device-icon {
    background: linear-gradient(135deg, #FF6B35 0%, #FFA07A 100%);
}

.device-card.ventilation .device-icon {
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
}

.device-card.blinds .device-icon {
    background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
}

.device-card.access .device-icon {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.device-card.energy .device-icon {
    background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
}

.device-status {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: #ecfdf5;
    color: #059669;
    font-size: 12px;
    font-weight: 600;
    border-radius: 20px;
}

.device-status.online .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #10b981;
    animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
}

.device-card h4 {
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 12px 0;
}

.device-card > p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.7;
    margin: 0 0 20px 0;
}

.device-features {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 20px;
}

.feature-row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #4b5563;
    font-weight: 500;
}

.feature-row .v-icon {
    color: #10b981;
}

.device-stats {
    display: flex;
    gap: 16px;
    padding-top: 20px;
    border-top: 2px solid #f3f4f6;
}

.stat {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.stat-label {
    font-size: 11px;
    color: #9ca3af;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.stat-value {
    font-size: 18px;
    font-weight: 800;
    color: #1f2937;
}

/* Control Features */
.features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 24px;
}

.control-feature {
    background: white;
    border-radius: 20px;
    padding: 28px;
    text-align: center;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    border: 2px solid transparent;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.control-feature:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
    border-color: #1E5A8E;
}

.feature-icon-wrapper {
    width: 72px;
    height: 72px;
    border-radius: 16px;
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    transition: transform 0.4s ease;
}

.control-feature:hover .feature-icon-wrapper {
    transform: scale(1.1) rotate(5deg);
}

.control-feature h4 {
    font-size: 18px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 12px 0;
}

.control-feature p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.7;
    margin: 0;
}

/* Protocols */
.protocols-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 20px;
}

.protocol-card {
    background: white;
    border-radius: 16px;
    padding: 24px;
    text-align: center;
    transition: all 0.3s ease;
    border: 2px solid #f3f4f6;
}

.protocol-card.featured {
    background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
    border: 2px solid #1E5A8E;
    box-shadow: 0 4px 16px rgba(30, 90, 142, 0.12);
}

.protocol-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    border-color: #1E5A8E;
}

.protocol-card.featured:hover {
    box-shadow: 0 12px 32px rgba(30, 90, 142, 0.2);
}

.protocol-logo {
    font-size: 20px;
    font-weight: 800;
    color: #1E5A8E;
    margin-bottom: 8px;
    letter-spacing: -0.5px;
}

.protocol-card.featured .protocol-logo {
    font-size: 22px;
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.protocol-card p {
    font-size: 12px;
    color: #6b7280;
    margin: 0;
}

/* Responsive */
@media (max-width: 1024px) {
    .hardware-container {
        padding: 32px 24px;
    }

    .flow-diagram {
        padding: 32px 24px;
    }
}

@media (max-width: 768px) {
    .card-header {
        padding: 24px;
        flex-direction: column;
        gap: 20px;
    }

    .card-header h2 {
        font-size: 24px;
    }

    .header-description {
        font-size: 14px;
    }

    .right-section {
        margin-left: 0;
        align-self: flex-end;
    }

    .hardware-container {
        padding: 24px 16px;
    }

    .section-header h3 {
        font-size: 24px;
    }

    .flow-diagram {
        padding: 24px 16px;
    }

    .flow-node {
        padding: 24px;
    }

    .features-grid {
        grid-template-columns: 1fr;
    }

    .protocols-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 480px) {
    .card-header h2 {
        font-size: 20px;
    }

    .hardware-container {
        padding: 20px 12px;
    }

    .device-card {
        padding: 20px;
    }
}

/* Scrollbar */
.hardware-container::-webkit-scrollbar {
    width: 8px;
}

.hardware-container::-webkit-scrollbar-track {
    background: #f3f4f6;
}

.hardware-container::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 4px;
}

.hardware-container::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
}
</style>